/**
 * Card media deferral (Padel USA perf phase 2b)
 * ---------------------------------------------------------------------------
 * Collection/product cards server-render only their first 3 visible slides with
 * full srcsets; later slides ship as placeholders carrying data-defer-src (a
 * single 1100w Shopify CDN URL — see snippets/card-media-deferred.liquid).
 *
 * Upgrade triggers (whichever fires first):
 *  1. IntersectionObserver — the moment a placeholder's slide actually becomes
 *     visible (covers product-card.js jumping variant-targeted cards to a later
 *     slide on load, programmatic selects, every reveal path). This is the
 *     correctness guarantee.
 *  2. First interaction with the card (hover / touch / focus / slide change) —
 *     pre-emptive, so by the time a shopper swipes to slide 4 it is already real.
 *
 * Document-level delegation + a MutationObserver re-registering placeholders
 * after section morphs (filtering, pagination, AJAX re-renders).
 *
 * Docs: custom-code-padelusa/features/card-media-defer.md
 */

/** Must match the widths used by snippets/card-gallery.liquid for full slides. */
const WIDTHS = [165, 360, 535, 750, 940, 1100];

/** @param {HTMLImageElement} img */
function upgradeImage(img) {
  const base = img.getAttribute('data-defer-src');
  if (!base) return;
  const stem = base.replace(/([?&])width=\d+/, '$1width={w}');
  if (stem.includes('{w}')) {
    img.setAttribute(
      'srcset',
      WIDTHS.map((width) => `${stem.replace('{w}', String(width))} ${width}w`).join(', ')
    );
  }
  const sizes = img.getAttribute('data-defer-sizes');
  if (sizes) img.setAttribute('sizes', sizes);
  img.setAttribute('src', base);
  img.removeAttribute('data-defer-src');
  img.removeAttribute('data-defer-sizes');
  observer?.unobserve(img);
}

/** @param {Element | null} root */
function upgradeCard(root) {
  if (!root) return;
  root.querySelectorAll('img[data-defer-src]').forEach((img) => upgradeImage(/** @type {HTMLImageElement} */ (img)));
}

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) upgradeImage(/** @type {HTMLImageElement} */ (entry.target));
          });
        },
        { rootMargin: '50px' }
      )
    : null;

function observeAll() {
  if (!observer) return;
  document.querySelectorAll('img[data-defer-src]').forEach((img) => observer.observe(img));
}

/** @param {Event} event */
function fromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const card = target.closest('product-card');
  if (card) upgradeCard(card);
}

document.addEventListener('pointerenter', fromEvent, true);
document.addEventListener('touchstart', fromEvent, { capture: true, passive: true });
document.addEventListener('focusin', fromEvent, true);
// Arrow clicks / swipes dispatch slideshow:select — upgrade on the first slide change too.
document.addEventListener('slideshow:select', fromEvent);

observeAll();
// Section morphs (filters, pagination) insert fresh placeholders — re-register them.
new MutationObserver(() => observeAll()).observe(document.body, { childList: true, subtree: true });
